import { useState } from "react";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Analytics from "./pages/Analytics";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import AdminPanel from "./pages/AdminPanel";
import SignUp from "./pages/SignUp";
import "./index.css";

const DEMO_USERS = [
  { email: "admin@insightforge.io", password: "admin123", name: "James Doe", role: "Admin", avatar: "JD" },
  { email: "user@insightforge.io", password: "user123", name: "Sarah Khan", role: "Analyst", avatar: "SK" },
];

const styles = {
  bg: { minHeight: "100vh", background: "#0A0E17", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden", padding: 20, fontFamily: "'DM Sans','Segoe UI',sans-serif" },
  grid: { position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(110,231,183,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(110,231,183,0.03) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" },
  blob1: { position: "fixed", top: "-20%", right: "-10%", width: 480, height: 480, borderRadius: "50%", background: "radial-gradient(circle,rgba(110,231,183,0.07) 0%,transparent 70%)", pointerEvents: "none" },
  blob2: { position: "fixed", bottom: "-20%", left: "-10%", width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle,rgba(96,165,250,0.07) 0%,transparent 70%)", pointerEvents: "none" },
  card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "44px 38px", width: "100%", maxWidth: 430, backdropFilter: "blur(20px)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)", position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" },
  logoRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 30 },
  logoIcon: { width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#6EE7B7,#60A5FA)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 18px rgba(110,231,183,0.3)" },
  logoText: { fontSize: 20, fontWeight: 800, color: "#E8EDF5", letterSpacing: "-0.02em" },
  title: { fontSize: 25, fontWeight: 800, color: "#E8EDF5", marginBottom: 8, textAlign: "center" },
  sub: { fontSize: 14, color: "#8892a4", marginBottom: 22, textAlign: "center" },
  hint: { background: "rgba(110,231,183,0.05)", border: "1px solid rgba(110,231,183,0.13)", borderRadius: 10, padding: "9px 16px", marginBottom: 26, width: "100%", textAlign: "center" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#C8D0DC", marginBottom: 7, width: "100%" },
  inputWrap: { position: "relative", display: "flex", alignItems: "center", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, transition: "all 0.2s ease" },
  iIcon: { position: "absolute", left: 13, pointerEvents: "none" },
  inp: { width: "100%", background: "none", border: "none", outline: "none", color: "#E8EDF5", fontSize: 14, padding: "12px 14px 12px 40px", fontFamily: "inherit" },
  err: { background: "rgba(248,113,113,0.09)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: "10px 14px", color: "#F87171", fontSize: 13, marginBottom: 14, textAlign: "center", width: "100%" },
  btn: { width: "100%", padding: "13px", borderRadius: 12, background: "linear-gradient(135deg,#6EE7B7,#34D399)", border: "none", color: "#0A0E17", fontSize: 15, fontWeight: 800, cursor: "pointer", transition: "all 0.2s ease", boxShadow: "0 4px 15px rgba(110,231,183,0.2)", fontFamily: "inherit" },
};

function SignInScreen({ onSwitch, login }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    setTimeout(() => {
      const found = DEMO_USERS.find(u => u.email === form.email && u.password === form.password);
      if (found) { const { password, ...safe } = found; login(safe); }
      else setError("Invalid credentials. Try: admin@insightforge.io / admin123");
      setLoading(false);
    }, 800);
  };

  return (
    <div style={styles.bg}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); *{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={styles.grid}/><div style={styles.blob1}/><div style={styles.blob2}/>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <div style={styles.logoIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <span style={styles.logoText}>InsightForge</span>
        </div>
        <h1 style={styles.title}>Welcome back</h1>
        <p style={styles.sub}>Sign in to your analytics dashboard</p>
        <div style={styles.hint}>
          <span style={{color:"#6EE7B7",fontSize:12}}>🔑 Demo: </span>
          <span style={{color:"#8892a4",fontSize:12}}>admin@insightforge.io / admin123</span>
        </div>
        <form onSubmit={submit} style={{width:"100%"}}>
          <div style={{marginBottom:16}}>
            <label style={styles.label}>Email Address</label>
            <div style={styles.inputWrap}>
              <svg style={styles.iIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8892a4" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <input type="email" placeholder="you@example.com" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} style={styles.inp}
                onFocus={e=>{e.target.parentNode.style.borderColor="rgba(110,231,183,0.5)";}}
                onBlur={e=>{e.target.parentNode.style.borderColor="rgba(255,255,255,0.1)";}} required/>
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrap}>
              <svg style={styles.iIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8892a4" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <input type={showPw?"text":"password"} placeholder="••••••••" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} style={styles.inp}
                onFocus={e=>{e.target.parentNode.style.borderColor="rgba(110,231,183,0.5)";}}
                onBlur={e=>{e.target.parentNode.style.borderColor="rgba(255,255,255,0.1)";}} required/>
              <button type="button" onClick={()=>setShowPw(v=>!v)} style={{position:"absolute",right:12,background:"none",border:"none",cursor:"pointer",color:"#8892a4",fontSize:12,fontWeight:600,fontFamily:"inherit"}}>{showPw?"Hide":"Show"}</button>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:20,marginTop:10}}>
            <label style={{display:"flex",gap:7,alignItems:"center",cursor:"pointer"}}>
              <input type="checkbox" style={{accentColor:"#6EE7B7"}}/>
              <span style={{fontSize:13,color:"#8892a4"}}>Remember me</span>
            </label>
            <span style={{fontSize:13,color:"#6EE7B7",cursor:"pointer"}}>Forgot password?</span>
          </div>
          {error&&<div style={styles.err}>{error}</div>}
          <button type="submit" disabled={loading} style={{...styles.btn,opacity:loading?0.75:1}}
            onMouseEnter={e=>{if(!loading){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 12px 30px rgba(110,231,183,0.4)";}}}
            onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 4px 15px rgba(110,231,183,0.2)";}}>
            {loading?"Signing in...":"Sign In →"}
          </button>
        </form>
        <p style={{marginTop:22,fontSize:14,color:"#8892a4",textAlign:"center"}}>
          No account? <span onClick={onSwitch} style={{color:"#6EE7B7",cursor:"pointer",fontWeight:700}}>Create one free</span>
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(()=>{
    try{const s=localStorage.getItem("if_user");return s?JSON.parse(s):null;}catch{return null;}
  });
  const [page, setPage] = useState("dashboard");
  const [authMode, setAuthMode] = useState("signin");

  const login = (userData) => { localStorage.setItem("if_user",JSON.stringify(userData)); setAuth(userData); setPage("dashboard"); };
  const logout = () => { localStorage.removeItem("if_user"); setAuth(null); };

  if (!auth) {
    if (authMode === "signup") return <SignUp onSwitch={()=>setAuthMode("signin")} login={login}/>;
    return <SignInScreen onSwitch={()=>setAuthMode("signup")} login={login}/>;
  }

  const PAGES = {
    dashboard: <Dashboard user={auth} setPage={setPage}/>,
    analytics: <Analytics user={auth}/>,
    users: <Users user={auth}/>,
    settings: <Settings user={auth}/>,
    admin: <AdminPanel user={auth}/>,
  };

  return (
    <Layout page={page} setPage={setPage} onLogout={logout} user={auth}>
      {PAGES[page]||PAGES.dashboard}
    </Layout>
  );
}